import { useEffect, useState } from "react";
import { UserPlus, Trash2, KeyRound, Pencil, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { SectionTitle, Card } from "../ui";

const FIELD =
  "w-full bg-white border border-sand-200 rounded-lg px-3 py-2 text-sm text-charcoal-800 focus:outline-none focus:ring-2 focus:ring-sage/50";

function TempPassword({ value }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div
      data-testid="team-temp-password"
      className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-sage/40 bg-sage-light px-4 py-3"
    >
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wider text-charcoal-500">Temporary password — share securely</p>
        <p className="font-mono text-sm text-charcoal-800 break-all">{value}</p>
      </div>
      <button
        data-testid="team-copy-password"
        onClick={copy}
        className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-sage/50 px-3 py-1.5 text-xs font-medium text-charcoal-700 hover:bg-white"
      >
        {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

export default function TeamManager() {
  const [rows, setRows] = useState([]);
  const [neu, setNeu] = useState({ email: "", name: "", password: "", admin_role: "scheduler_admin" });
  const [newTemp, setNewTemp] = useState(null);
  const [editing, setEditing] = useState({}); // id -> name draft
  const [resetTemp, setResetTemp] = useState({}); // id -> temp password

  const load = () => api.get("/admin/users").then((res) => setRows(res.data));
  useEffect(() => {
    load();
  }, []);

  const ROLES = [
    ["owner", "Owner — full control"],
    ["content_admin", "Content admin"],
    ["scheduler_admin", "Scheduler admin"],
    ["support_admin", "Support admin"],
  ];
  const changeRole = async (id, admin_role) => {
    try {
      await api.put(`/admin/users/${id}`, { admin_role });
      toast.success("Role updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update role");
    }
  };

  const add = async () => {
    if (!neu.email || !neu.name) {
      toast.error("Name and email are required");
      return;
    }
    try {
      const res = await api.post("/admin/users", neu);
      toast.success(`${res.data.name} added as admin`);
      setNeu({ email: "", name: "", password: "", admin_role: "scheduler_admin" });
      setNewTemp(neu.password ? null : { id: res.data.id, value: res.data.temp_password });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add admin");
    }
  };

  const saveName = async (id) => {
    try {
      await api.put(`/admin/users/${id}`, { name: editing[id] });
      toast.success("Name updated");
      setEditing((s) => {
        const n = { ...s };
        delete n[id];
        return n;
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  const resetPassword = async (id, name) => {
    if (!window.confirm(`Reset password for ${name}? They'll be asked to set a new one on next login.`)) return;
    try {
      const res = await api.put(`/admin/users/${id}`, { new_password: "" });
      setResetTemp((s) => ({ ...s, [id]: res.data.temp_password }));
      toast.success("Password reset");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reset password");
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Remove ${name}? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("Admin removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove admin");
    }
  };

  return (
    <div data-testid="admin-team">
      <SectionTitle title="Team / Admins" subtitle="Invite, rename, and remove admins, or reset their passwords." />

      <Card className="mb-6">
        <h3 className="font-medium text-charcoal-800 mb-4">Add an admin</h3>
        <div className="space-y-3">
          <input
            data-testid="team-new-name"
            className={FIELD}
            placeholder="Full name"
            value={neu.name}
            onChange={(e) => setNeu({ ...neu, name: e.target.value })}
          />
          <input
            data-testid="team-new-email"
            className={FIELD}
            placeholder="Email address"
            value={neu.email}
            onChange={(e) => setNeu({ ...neu, email: e.target.value })}
          />
          <input
            data-testid="team-new-password"
            className={FIELD}
            placeholder="Temporary password (optional — auto-generated if blank)"
            value={neu.password}
            onChange={(e) => setNeu({ ...neu, password: e.target.value })}
          />
          <select
            data-testid="team-new-role"
            className={FIELD}
            value={neu.admin_role}
            onChange={(e) => setNeu({ ...neu, admin_role: e.target.value })}
          >
            {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button
            data-testid="team-add"
            onClick={add}
            className="inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-2 text-sm text-white font-medium hover:bg-terracotta-hover"
          >
            <UserPlus size={16} /> Add admin
          </button>
          {newTemp && <TempPassword value={newTemp.value} />}
        </div>
      </Card>

      <div className="space-y-4">
        {rows.map((u) => (
          <Card key={u.id}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                {editing[u.id] !== undefined ? (
                  <div className="flex gap-2">
                    <input
                      data-testid={`team-edit-name-${u.id}`}
                      className={FIELD}
                      value={editing[u.id]}
                      onChange={(e) => setEditing((s) => ({ ...s, [u.id]: e.target.value }))}
                    />
                    <button
                      data-testid={`team-save-name-${u.id}`}
                      onClick={() => saveName(u.id)}
                      className="shrink-0 rounded-full bg-sage px-4 py-2 text-sm text-white font-medium hover:bg-sage-hover"
                    >
                      Save
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-charcoal-800">{u.name}</p>
                    {u.is_self && (
                      <span className="rounded-full bg-sand-100 px-2 py-0.5 text-xs text-charcoal-500">You</span>
                    )}
                    {u.must_change_password && (
                      <span className="rounded-full bg-terracotta-light px-2 py-0.5 text-xs text-terracotta-700">
                        Pending first login
                      </span>
                    )}
                  </div>
                )}
                <p className="mt-1 text-sm text-charcoal-500 break-all">{u.email}</p>
                <select
                  data-testid={`team-role-${u.id}`}
                  value={u.admin_role || "owner"}
                  onChange={(e) => changeRole(u.id, e.target.value)}
                  className="mt-2 bg-white border border-sand-200 rounded-lg px-2 py-1 text-xs text-charcoal-700 focus:outline-none focus:ring-2 focus:ring-sage/50"
                >
                  {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>

              <div className="flex shrink-0 items-center gap-1">
                <button
                  data-testid={`team-rename-${u.id}`}
                  title="Rename"
                  onClick={() =>
                    setEditing((s) => (s[u.id] !== undefined ? s : { ...s, [u.id]: u.name }))
                  }
                  className="rounded-lg p-2 text-charcoal-500 hover:bg-sand-100 hover:text-charcoal-800"
                >
                  <Pencil size={16} />
                </button>
                <button
                  data-testid={`team-reset-${u.id}`}
                  title="Reset password"
                  onClick={() => resetPassword(u.id, u.name)}
                  className="rounded-lg p-2 text-charcoal-500 hover:bg-sand-100 hover:text-charcoal-800"
                >
                  <KeyRound size={16} />
                </button>
                {!u.is_self && (
                  <button
                    data-testid={`team-delete-${u.id}`}
                    title="Remove admin"
                    onClick={() => remove(u.id, u.name)}
                    className="rounded-lg p-2 text-charcoal-400 hover:bg-sand-100 hover:text-destructive"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
            {resetTemp[u.id] && <TempPassword value={resetTemp[u.id]} />}
          </Card>
        ))}
      </div>
    </div>
  );
}
